/** The itinerary's rules (the old editor's `validateDays`), as issues on the inputs they
 *  concern (plan §5): a day covers at least one day; a stop has a valid time, and no other
 *  stop of its day shares it; an activity row names an activity; prices are numbers and a
 *  range does not end below its start.
 *
 *  An issue BLOCKS Save (an invalid input, with the reason on the input) only where the
 *  draft causes it — a new row, or a value the draft changed (for a shared time, any stop of
 *  the clash). A tour stored like that (another editor, an import) reads as an amber note
 *  and never blocks an unrelated save: the server accepts it, and the funnel is not sent
 *  the stored values back.
 *
 *  Pure, over the draft's entries and the baselines. */
import type { RecordKey } from '@/features/records/draft/changeSet';
import type { OutlineIssue } from '@/features/records/outline/issues';

import {
  activityRec,
  DAY_SLOTS,
  dayRec,
  DAYS_PATH,
  listIn,
  SLOT_ACTIVITIES,
  slotRec,
  TIME_RE,
  valueIn,
  type Baselines,
  type Entries,
} from './model';

/** One finding of the itinerary's rules (the records outline's issue: `blocking` = caused
 *  by the draft, Save is blocked until it is fixed). */
export type ItineraryIssue = OutlineIssue;

const PRICE_RE = /^\d+([.,]\d{1,2})?$/;

export function validateItinerary(entries: Entries | null, baselines: Baselines, tourRec: RecordKey): ItineraryIssue[] {
  const out: ItineraryIssue[] = [];
  const value = (rec: RecordKey, path: string) => valueIn(entries, baselines, rec, path);
  const touched = (rec: RecordKey, path: string) => rec.includes(':-') || Boolean(entries?.[rec]?.[path]);

  listIn(entries, baselines, tourRec, DAYS_PATH).forEach((dayId, di) => {
    const dRec = dayRec(dayId);
    const span = value(dRec, 'day.day_span');
    if (typeof span !== 'number' || !Number.isInteger(span) || span < 1) {
      out.push({ rec: dRec, path: 'day.day_span', message: 'At least 1 day', blocking: touched(dRec, 'day.day_span') });
    }
    const byTime = new Map<string, RecordKey[]>();
    const storedSlots = baselines[dRec]?.[DAY_SLOTS];
    /** The draft brought the stop here (new, its time changed, moved in from another day). */
    const clashCause = (sRec: RecordKey, slotId: number) =>
      touched(sRec, 'slot.time') || !(Array.isArray(storedSlots) && (storedSlots as number[]).includes(slotId));
    const causes = new Set<RecordKey>();
    for (const slotId of listIn(entries, baselines, dRec, DAY_SLOTS)) {
      const sRec = slotRec(slotId);
      const time = value(sRec, 'slot.time');
      if (typeof time !== 'string' || !TIME_RE.test(time)) {
        out.push({ rec: sRec, path: 'slot.time', message: 'Enter a time (HH:MM)', blocking: touched(sRec, 'slot.time') });
      } else {
        byTime.set(time, [...(byTime.get(time) ?? []), sRec]);
        if (entries && clashCause(sRec, slotId)) causes.add(sRec);
      }
      for (const activityId of listIn(entries, baselines, sRec, SLOT_ACTIVITIES)) {
        const aRec = activityRec(activityId);
        if (typeof value(aRec, 'activity.activity') !== 'number') {
          out.push({
            rec: aRec,
            path: 'activity.activity',
            message: 'Choose an activity — or remove the row',
            blocking: touched(aRec, 'activity.activity'),
          });
        }
        const prices: Array<[string, number | null]> = [];
        for (const path of ['activity.cost_min', 'activity.cost_max']) {
          const raw = value(aRec, path);
          const t = typeof raw === 'string' ? raw.trim() : raw == null ? '' : String(raw);
          if (t === '') {
            prices.push([path, null]);
          } else if (!PRICE_RE.test(t)) {
            out.push({ rec: aRec, path, message: 'Enter an amount, e.g. 12.50', blocking: touched(aRec, path) });
            prices.push([path, null]);
          } else {
            prices.push([path, Number(t.replace(',', '.'))]);
          }
        }
        const [min, max] = [prices[0]![1], prices[1]![1]];
        if (min !== null && max !== null && max < min) {
          out.push({
            rec: aRec,
            path: 'activity.cost_max',
            message: 'Below the min price',
            blocking: touched(aRec, 'activity.cost_max') || touched(aRec, 'activity.cost_min'),
          });
        }
      }
    }
    for (const [time, recs] of byTime) {
      if (recs.length < 2) continue;
      const blocking = recs.some((r) => causes.has(r));
      for (const rec of recs) {
        out.push({ rec, path: 'slot.time', message: `Another stop of Day ${di + 1} is at ${time}`, blocking });
      }
    }
  });
  return out;
}

/** A stable key of an issue list (the validator re-renders only when it changes). */
export function issuesKey(issues: readonly ItineraryIssue[]): string {
  return issues.map((i) => `${i.rec}|${i.path}|${i.blocking ? 1 : 0}|${i.message}`).join('\n');
}
